import React, { useEffect, useRef, useState, MouseEvent } from "react";
import classNames from "classnames";
import { TypewriterText } from "../../common/components/TypewriterText";
import { MatrixHero } from "../../common/components/MatrixHero";
import "../../scss/proposals.scss";
import { useI18n } from "../../i18n/I18nProvider";

// ==================== HOOKS ====================

function useInView(threshold = 0.15) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (ref.current) observer.unobserve(ref.current);
                }
            },
            { threshold }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);
    return { ref, inView };
}

// ==================== CONSTANTS ====================

const CONTACT_EMAIL = "cc@wsiz.edu.pl";

// ==================== COMPONENT ====================

const ProposalsPage: React.FC = () => {
    const [ready, setReady] = useState(false);
    const { t } = useI18n();

    const thesisItems = [
        t("proposals_thesis_1"),
        t("proposals_thesis_2"),
        t("proposals_thesis_3"),
        t("proposals_thesis_4"),
        t("proposals_thesis_5"),
    ];

    const projectItems = [
        t("proposals_projects_1"),
        t("proposals_projects_2"),
        t("proposals_projects_3"),
        t("proposals_projects_4"),
        t("proposals_projects_5"),
    ];

    const { ref: refIntro, inView: inViewIntro } = useInView(0.15);
    const { ref: refThesis, inView: inViewThesis } = useInView(0.15);
    const { ref: refProjects, inView: inViewProjects } = useInView(0.15);
    const { ref: refCta, inView: inViewCta } = useInView(0.15);

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);

    const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        target.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
        target.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    };

    return (
        <section
            className={classNames("prop-page", { "prop-page--ready": ready })}
            onMouseMove={handleMouseMove}
            aria-label={t("proposals_section_label")}
        >
            <div className="grid-overlay" aria-hidden="true" />
            <div className="grid-glow" aria-hidden="true" />

            <MatrixHero
                title={t("proposals_hero_title")}
                subtitle={t("proposals_hero_subtitle")}
                density={0.5}
            />

            <div className="prop-page__inner">

                {/* Intro */}
                <article
                    ref={refIntro}
                    className={classNames("prop-card", { "prop-card--visible": inViewIntro })}
                >
                    <div className="prop-card__glow" aria-hidden="true" />
                    <h2 className="prop-card__title">{t("proposals_intro_title")}</h2>
                    <p className="prop-card__text">
                        <TypewriterText text={t("proposals_intro_text")} active={inViewIntro} speed={4} />
                    </p>
                </article>

                {/* Tiles grid */}
                <div className="prop-tiles">

                    {/* Tile 1 – Prace dyplomowe */}
                    <article
                        ref={refThesis}
                        className={classNames("prop-card prop-card--accent", { "prop-card--visible": inViewThesis })}
                    >
                        <div className="prop-card__glow" aria-hidden="true" />
                        <h2 className="prop-card__title">{t("proposals_thesis_title")}</h2>
                        <p className="prop-card__text">{t("proposals_thesis_text")}</p>
                        <ul className="prop-list">
                            {thesisItems.map((item, i) => (
                                <li key={i} className="prop-list__item" style={{ animationDelay: `${i * 0.08}s` }}>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </article>

                    {/* Tile 2 – Wasze projekty */}
                    <article
                        ref={refProjects}
                        className={classNames("prop-card prop-card--accent", { "prop-card--visible": inViewProjects })}
                    >
                        <div className="prop-card__glow" aria-hidden="true" />
                        <h2 className="prop-card__title">{t("proposals_projects_title")}</h2>
                        <p className="prop-card__text">{t("proposals_projects_text")}</p>
                        <ul className="prop-list">
                            {projectItems.map((item, i) => (
                                <li key={i} className="prop-list__item" style={{ animationDelay: `${i * 0.08}s` }}>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </article>

                </div>

                {/* CTA */}
                <div
                    ref={refCta}
                    className={classNames("prop-cta", { "prop-cta--visible": inViewCta })}
                >
                    <h2 className="prop-cta__heading">{t("proposals_cta_heading")}</h2>
                    <p className="prop-cta__subtext">{t("proposals_cta_subtext")}</p>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="prop-cta__email">
                        {CONTACT_EMAIL}
                    </a>
                </div>

            </div>
        </section>
    );
};

export default ProposalsPage;
